/**
 * How the scope rides the URL.
 *
 * Adapted from upstream's `settingsScopeNavigation.ts` (pingdotgg/t3code,
 * MIT, pin e3e7cc3f). The middleware is the load-bearing half: moving between
 * settings categories keeps the selected target, while a navigation that
 * names an axis explicitly replaces the whole previous selection. Without
 * that split, either the sentence resets on every page change or a stale
 * project follows the user into a fresh pick.
 *
 * @module
 */

import type { SearchMiddleware } from "@tanstack/react-router";

import { validateSettingsScopeSearch, type SettingsScopeSearch } from "./settingsScope";

/** The route's search validator: only the two axes are valid search state. */
export function validateSettingsRouteSearch(raw: Record<string, unknown>): SettingsScopeSearch {
  return validateSettingsScopeSearch(raw);
}

const SCOPE_KEYS = ["project", "machine"] as const satisfies readonly (keyof SettingsScopeSearch)[];

/**
 * Category links keep the target; an explicit target replaces it. "Explicit"
 * means the next search carries one of the axis keys at all, even set to
 * undefined, which is how the scope menus write "all".
 */
export const retainSettingsScope: SearchMiddleware<SettingsScopeSearch> = ({ search, next }) => {
  const result = next(search);
  if (SCOPE_KEYS.some((key) => Object.hasOwn(result, key))) return result;
  const previousScope = Object.fromEntries(
    SCOPE_KEYS.filter((key) => search[key] !== undefined).map((key) => [key, search[key]]),
  );
  return { ...previousScope, ...result };
};
